import React, { useEffect, useRef, useState, useMemo } from "react";
import { api, getErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import BedeeSearchField from "@/components/BedeeSearchField";
import { PdfSplitter } from "@/components/PdfSplitter";
import {
  FileText, Sparkles, Loader2, Check, Mail, Trash2,
  AlertTriangle, ExternalLink, X, ArrowRight, Scissors,
} from "lucide-react";
import { toast } from "sonner";
import { displayValue, parseValue, placeholderFor, formatsFor, parseDateInput, formatFileSize } from "@/lib/format";

export default function DocumentQueue() {
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [metadata, setMetadata] = useState({});
  const [signedUrl, setSignedUrl] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [classifyLoading, setClassifyLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [activeEntrepriseId, setActiveEntrepriseId] = useState(null);
  const [dupPopoverId, setDupPopoverId] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [typeSearch, setTypeSearch] = useState("");
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const typeComboRef = useRef(null);
  const [splitMode, setSplitMode] = useState(false);
  // Filtre : "tous" (défaut = tout visible) | "a_valider" (IA a une suggestion)
  const [queueFilter, setQueueFilter] = useState("tous");

  const loadQueue = () =>
    api.get("/documents", { params: { status: "pending" } }).then((r) => {
      setDocs(r.data);
      // Nettoyer les cases cochées qui n'existent plus
      setCheckedIds(prev => {
        const ids = new Set(r.data.map(d => d.id));
        return new Set([...prev].filter(id => ids.has(id)));
      });
    });

  useEffect(() => {
    Promise.all([
      loadQueue(),
      api.get("/document-types").then((r) => setTypes(r.data)),
      api.get("/societes").then((r) => setSocietes(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedId) { setSignedUrl(null); return; }
    api.get(`/documents/${selectedId}/signed-url`).then((r) => setSignedUrl(r.data.url));
  }, [selectedId]);

  // Fermer le combobox type au clic extérieur
  useEffect(() => {
    const handler = (e) => {
      if (typeComboRef.current && !typeComboRef.current.contains(e.target))
        setTypeDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedType = useMemo(
    () => types.find((t) => t.id === selectedTypeId) || null,
    [types, selectedTypeId]
  );
  const selectedDoc = useMemo(() => docs.find((d) => d.id === selectedId) || null, [docs, selectedId]);

  const allChecked = docs.length > 0 && checkedIds.size === docs.length;
  const someChecked = checkedIds.size > 0 && !allChecked;

  const toggleCheck = (e, docId) => {
    e.stopPropagation();
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(docId) ? next.delete(docId) : next.add(docId);
      return next;
    });
  };

  const toggleAll = () => {
    if (allChecked) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(docs.map(d => d.id)));
    }
  };

  const selectDoc = (d) => {
    setSelectedId(d.id);
    // Pré-remplir depuis la suggestion IA si disponible
    setSelectedTypeId(d.ai_document_type_id || "");
    setMetadata(d.ai_metadata || {});
    setActiveEntrepriseId(d.ai_societe_id || null);
    setTypeSearch("");
    setTypeDropdownOpen(false);
    setSplitMode(false);
  };

  const runOCR = async () => {
    if (!selectedId || !selectedTypeId) {
      toast.error("Sélectionnez un type de document.");
      return;
    }
    setOcrLoading(true);
    try {
      const r = await api.post(`/documents/${selectedId}/ocr`, { document_type_id: selectedTypeId });
      setMetadata((prev) => ({ ...prev, ...(r.data.metadata || {}) }));
      toast.success("Métadonnées extraites par Gemini.");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setOcrLoading(false);
    }
  };

  const classify = async (force = false) => {
    if (!selectedId || !selectedTypeId) return;
    setClassifyLoading(true);
    try {
      await api.put(`/documents/${selectedId}/classify`, {
        document_type_id: selectedTypeId,
        metadata,
        force,
      });
      toast.success("Document classé.");
      setSelectedId(null);
      setSelectedTypeId("");
      setMetadata({});
      setDuplicateWarning(null);
      await loadQueue();
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.detail?.type === "duplicate_classified_filename") {
        setDuplicateWarning(err.response.data.detail);
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setClassifyLoading(false);
    }
  };

  const deleteDoc = async (docId, filename) => {
    if (!confirm(`Supprimer définitivement « ${filename} » ? Cette action est irréversible.`)) return;
    setDeletingId(docId);
    try {
      await api.delete(`/documents/${docId}`);
      toast.success("Document supprimé.");
      if (selectedId === docId) { setSelectedId(null); setSelectedTypeId(""); setMetadata({}); }
      await loadQueue();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  };

  const bulkDelete = async () => {
    const count = checkedIds.size;
    if (!confirm(`Supprimer définitivement ${count} document(s) sélectionné(s) ? Cette action est irréversible.`)) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...checkedIds].map(id => api.delete(`/documents/${id}`)));
      toast.success(`${count} document(s) supprimé(s).`);
      if (checkedIds.has(selectedId)) { setSelectedId(null); setSelectedTypeId(""); setMetadata({}); }
      setCheckedIds(new Set());
      await loadQueue();
    } catch (err) {
      toast.error(getErrorMessage(err));
      await loadQueue();
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title="File de classement"
        subtitle={`${docs.length} document(s) en attente de classement`}
        index="03"
      />
      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] border-b border-zinc-200 min-h-[calc(100vh-89px)]">
        {/* Queue list */}
        <aside className="border-r border-zinc-200 overflow-auto app-scroll flex flex-col" data-testid="queue-list">
          {/* Header avec select-all */}
          <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50/50 flex items-center gap-3 sticky top-0 z-[2]">
            {docs.length > 0 && (
              <button
                data-testid="queue-select-all"
                onClick={toggleAll}
                title={allChecked ? "Tout désélectionner" : "Tout sélectionner"}
                className={`w-4 h-4 border flex items-center justify-center shrink-0 transition-colors ${
                  allChecked ? "bg-[#002FA7] border-[#002FA7]"
                  : someChecked ? "bg-[#002FA7]/20 border-[#002FA7]"
                  : "border-zinc-300 hover:border-zinc-500"
                }`}
              >
                {allChecked && <Check size={10} strokeWidth={2.5} className="text-white" />}
                {someChecked && <span className="w-2 h-0.5 bg-[#002FA7]" />}
              </button>
            )}
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono flex-1">
              À classer · {docs.length}
            </span>
          </div>

          {/* Filtre À valider / Tous */}
          {docs.length > 0 && (() => {
            const aiCount = docs.filter(d => d.ai_document_type_id).length;
            return (
              <div className="flex border-b border-zinc-200 shrink-0" data-testid="queue-filter">
                <button
                  data-testid="filter-tous"
                  onClick={() => setQueueFilter("tous")}
                  className={`flex-1 py-2 text-[11px] font-mono font-medium transition-colors flex items-center justify-center gap-1.5
                    ${queueFilter === "tous"
                      ? "text-[#002FA7] border-b-2 border-[#002FA7] -mb-px bg-[#002FA7]/3"
                      : "text-zinc-500 hover:text-zinc-800"}`}
                >
                  Tous
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-mono
                    ${queueFilter === "tous" ? "bg-[#002FA7] text-white" : "bg-zinc-200 text-zinc-600"}`}>
                    {docs.length}
                  </span>
                </button>
                <button
                  data-testid="filter-a-valider"
                  onClick={() => setQueueFilter("a_valider")}
                  className={`flex-1 py-2 text-[11px] font-mono font-medium transition-colors flex items-center justify-center gap-1.5
                    ${queueFilter === "a_valider"
                      ? "text-[#002FA7] border-b-2 border-[#002FA7] -mb-px bg-[#002FA7]/3"
                      : "text-zinc-500 hover:text-zinc-800"}`}
                >
                  <Sparkles size={10} strokeWidth={2} />
                  Pré-remplis IA
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-mono
                    ${queueFilter === "a_valider" ? "bg-[#002FA7] text-white" : "bg-zinc-200 text-zinc-600"}`}>
                    {aiCount}
                  </span>
                </button>
              </div>
            );
          })()}

          {/* Barre d'action contextuelle */}
          {checkedIds.size > 0 && (
            <div
              data-testid="bulk-action-bar"
              className="px-4 py-2.5 border-b border-zinc-200 bg-red-50 flex items-center justify-between gap-2"
            >
              <span className="text-xs font-mono text-zinc-600">
                {checkedIds.size} sélectionné{checkedIds.size > 1 ? "s" : ""}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCheckedIds(new Set())}
                  className="text-xs font-mono text-zinc-500 hover:text-zinc-800 transition-colors"
                >
                  Annuler
                </button>
                <button
                  data-testid="bulk-delete-btn"
                  onClick={bulkDelete}
                  disabled={bulkDeleting}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#DC2626] hover:bg-[#B91C1C] px-3 py-1.5 transition-colors disabled:opacity-50"
                >
                  {bulkDeleting
                    ? <Loader2 size={11} strokeWidth={2} className="animate-spin" />
                    : <Trash2 size={11} strokeWidth={2} />
                  }
                  {bulkDeleting ? "Suppression…" : "Supprimer la sélection"}
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="p-6 text-sm text-zinc-500">Chargement…</div>
          ) : docs.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">
              Aucun document en attente.<br />Déposez-en dans le SAS.
            </div>
          ) : (
            <ul>
              {docs
                .filter(d => queueFilter === "tous" || d.ai_document_type_id != null)
                .map((d) => {
                const isChecked = checkedIds.has(d.id);
                const hasDuplicates = d.duplicate_originals?.length > 0;
                const aiPct = d.ai_confidence != null ? Math.round(d.ai_confidence * 100) : null;
                return (
                  <li key={d.id} className="group relative">
                    <div className={`flex items-stretch border-b border-zinc-100 transition-colors ${
                      isChecked ? "bg-[#DC2626]/5" :
                      selectedId === d.id ? "bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]" : "hover:bg-zinc-50"
                    }`}>
                      {/* Checkbox */}
                      <button
                        data-testid={`queue-check-${d.id}`}
                        onClick={(e) => toggleCheck(e, d.id)}
                        className="px-3 flex items-center justify-center shrink-0"
                        aria-label="Sélectionner"
                      >
                        <span className={`w-4 h-4 border flex items-center justify-center transition-colors ${
                          isChecked ? "bg-[#DC2626] border-[#DC2626]" : "border-zinc-300 group-hover:border-zinc-400"
                        }`}>
                          {isChecked && <Check size={10} strokeWidth={2.5} className="text-white" />}
                        </span>
                      </button>

                      {/* Item cliquable */}
                      <button
                        data-testid={`queue-item-${d.id}`}
                        onClick={() => selectDoc(d)}
                        className="flex-1 text-left py-3 pr-10 flex items-start gap-3"
                      >
                        <FileText size={16} strokeWidth={1.5} className="text-zinc-500 mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{d.original_filename}</div>
                          <div className="text-[11px] text-zinc-500 font-mono mt-0.5 flex items-center gap-1.5 flex-wrap">
                            {d.source === "email" && d.inbound_from && (
                              <span className="inline-flex items-center gap-1 text-[#002FA7]">
                                <Mail size={10} strokeWidth={1.5} />
                                <span className="truncate max-w-[120px]">{d.inbound_from}</span>
                              </span>
                            )}
                            {d.source === "upload" && d.uploaded_by_name && (
                              <span className="truncate max-w-[120px] text-zinc-600">{d.uploaded_by_name}</span>
                            )}
                            {(d.inbound_from || d.uploaded_by_name) && <span className="text-zinc-300">·</span>}
                            <span>{new Date(d.created_at).toLocaleString("fr-FR")}</span>
                            {d.size_bytes != null && (
                              <>
                                <span className="text-zinc-300">·</span>
                                <span data-testid={`queue-size-${d.id}`}>{formatFileSize(d.size_bytes)}</span>
                              </>
                            )}
                            {d.page_count != null && (
                              <>
                                <span className="text-zinc-300">·</span>
                                <span data-testid={`queue-pages-${d.id}`}>{d.page_count} p.</span>
                              </>
                            )}
                          </div>

                          {/* Badge IA */}
                          {d.ai_document_type_id && (
                            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                              <span
                                data-testid={`ai-badge-${d.id}`}
                                className={`inline-flex items-center gap-1 text-[9px] font-mono font-semibold px-1.5 py-0.5 border
                                  ${aiPct >= 90
                                    ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                                    : aiPct >= 70
                                    ? "bg-amber-50 border-amber-300 text-amber-700"
                                    : "bg-zinc-50 border-zinc-300 text-zinc-600"}`}
                              >
                                <Sparkles size={8} strokeWidth={2} />
                                IA {aiPct} %
                              </span>
                              <span className="text-[10px] text-zinc-500 truncate max-w-[120px]">
                                {d.ai_document_type_name}
                              </span>
                              {d.ai_societe_name && (
                                <span className="text-[10px] text-zinc-400 truncate max-w-[100px]">
                                  · {d.ai_societe_name}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Duplicate badge */}
                          {hasDuplicates && (
                            <div className="relative mt-1.5" onClick={(e) => e.stopPropagation()}>
                              <button
                                data-testid={`dup-badge-${d.id}`}
                                onClick={() => setDupPopoverId(dupPopoverId === d.id ? null : d.id)}
                                className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 bg-amber-50 border border-amber-300 text-amber-700 hover:bg-amber-100 transition-colors"
                              >
                                <AlertTriangle size={9} strokeWidth={2} className="shrink-0" />
                                {d.duplicate_originals.length} doublon{d.duplicate_originals.length > 1 ? "s" : ""}
                              </button>

                              {/* Duplicate popover */}
                              {dupPopoverId === d.id && (
                                <DuplicatePopover
                                  duplicates={d.duplicate_originals}
                                  onClose={() => setDupPopoverId(null)}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </button>

                      {/* Bouton supprimer individuel — visible au hover */}
                      <button
                        data-testid={`queue-delete-${d.id}`}
                        onClick={(e) => { e.stopPropagation(); deleteDoc(d.id, d.original_filename); }}
                        disabled={deletingId === d.id}
                        title="Supprimer"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-300 hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
                      >
                        {deletingId === d.id
                          ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
                          : <Trash2 size={14} strokeWidth={1.5} />
                        }
                      </button>
                    </div>
                  </li>
                );
              })}
              {/* Message si filtre "Pré-remplis IA" vide mais docs présents */}
              {queueFilter === "a_valider" && docs.filter(d => d.ai_document_type_id != null).length === 0 && (
                <li className="px-5 py-8 text-center text-xs text-zinc-400 font-mono">
                  Aucune analyse IA disponible pour le moment.<br />
                  <button onClick={() => setQueueFilter("tous")} className="underline mt-1">
                    Voir tous les documents
                  </button>
                </li>
              )}
            </ul>
          )}
        </aside>

        {/* Split: PDF + form — en mode découpe le splitter prend toute la largeur */}
        <section className={`min-h-[calc(100vh-89px)] ${splitMode ? "flex flex-col" : "grid grid-cols-1 md:grid-cols-2"}`}>
          {!splitMode && (
          <div className="border-r border-zinc-200 bg-zinc-100 min-h-[400px]" data-testid="pdf-viewer">
            {selectedId && signedUrl ? (
              <iframe
                key={signedUrl}
                src={signedUrl}
                title="PDF"
                className="pdf-embed"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center p-8 text-center">
                <div>
                  <FileText size={48} strokeWidth={1} className="text-zinc-300 mx-auto" />
                  <div className="mt-4 text-sm text-zinc-500">
                    {selectedId ? "Chargement du PDF..." : "Sélectionnez un document pour le prévisualiser."}
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          <div className={splitMode ? "flex-1 overflow-hidden" : "p-6 md:p-8 overflow-auto app-scroll"} data-testid="metadata-panel">
            {!selectedId ? (
              <div className="h-full flex items-center justify-center text-center">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">
                    Métadonnées
                  </div>
                  <div className="mt-2 text-sm text-zinc-500">Sélectionnez un document à classer.</div>
                </div>
              </div>
            ) : splitMode ? (
              /* ── Mode découpe ── */
              <PdfSplitter
                docId={selectedId}
                filename={selectedDoc?.original_filename || "document.pdf"}
                onDone={() => { setSplitMode(false); setSelectedId(null); loadQueue(); }}
                onCancel={() => setSplitMode(false)}
              />
            ) : (
              <div className="space-y-6">
                {/* Infos fichier + bouton Découper */}
                {selectedDoc && (
                  <div className="flex items-center justify-between gap-4 border-b border-zinc-100 pb-4">
                    <div className="flex items-center gap-4 text-[11px] font-mono text-zinc-500">
                      {selectedDoc.size_bytes != null && (
                        <span data-testid="classify-panel-size">{formatFileSize(selectedDoc.size_bytes)}</span>
                      )}
                      {selectedDoc.page_count != null && (
                        <span data-testid="classify-panel-pages">{selectedDoc.page_count} page{selectedDoc.page_count > 1 ? "s" : ""}</span>
                      )}
                    </div>
                    {selectedDoc.page_count > 1 && (
                      <button
                        data-testid="open-splitter-btn"
                        onClick={() => setSplitMode(true)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium border border-zinc-300 text-zinc-600 hover:border-[#002FA7] hover:text-[#002FA7] px-3 py-1.5 transition-colors shrink-0"
                        title="Découper ce PDF en plusieurs fichiers"
                      >
                        <Scissors size={12} strokeWidth={1.5} />
                        Découper
                      </button>
                    )}
                  </div>
                )}

                {/* Bannière suggestion IA */}
                {selectedDoc?.ai_document_type_id && (
                  <div
                    data-testid="ai-suggestion-banner"
                    className={`flex items-start gap-2.5 px-3 py-2.5 border text-xs
                      ${selectedDoc.ai_confidence >= 0.9
                        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                        : "bg-amber-50 border-amber-200 text-amber-800"}`}
                  >
                    <Sparkles size={12} strokeWidth={2} className="mt-0.5 shrink-0" />
                    <span>
                      Gemini a détecté <strong>{selectedDoc.ai_document_type_name}</strong> avec{" "}
                      <strong>{Math.round(selectedDoc.ai_confidence * 100)} %</strong> de confiance.
                      Les champs ont été pré-remplis automatiquement — vérifiez avant de classer.
                    </span>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">
                    Type de document
                  </div>
                  <div ref={typeComboRef} className="relative mt-2">
                    <input
                      data-testid="classify-type-select"
                      type="text"
                      value={selectedTypeId ? (types.find((t) => t.id === selectedTypeId)?.name ?? "") : typeSearch}
                      onChange={(e) => {
                        setTypeSearch(e.target.value);
                        setSelectedTypeId("");
                        setMetadata({});
                        setTypeDropdownOpen(true);
                      }}
                      onFocus={() => setTypeDropdownOpen(true)}
                      placeholder="Rechercher un type de document…"
                      className="w-full bg-white border border-zinc-300 px-4 py-2.5 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none placeholder:text-zinc-400"
                      autoComplete="off"
                    />
                    {typeDropdownOpen && (
                      <div className="absolute z-20 w-full bg-white border border-zinc-300 border-t-0 shadow-lg max-h-52 overflow-y-auto app-scroll">
                        {types
                          .filter((t) => t.status === "active" && t.name.toLowerCase().includes(typeSearch.toLowerCase()))
                          .map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setSelectedTypeId(t.id);
                                setTypeSearch("");
                                setMetadata({});
                                setTypeDropdownOpen(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                selectedTypeId === t.id ? "bg-[#cbe3ff]" : "hover:bg-[#e6f1ff]"
                              }`}
                            >
                              {t.name}
                            </button>
                          ))}
                        {types.filter((t) => t.status === "active" && t.name.toLowerCase().includes(typeSearch.toLowerCase())).length === 0 && (
                          <div className="px-4 py-2 text-sm text-zinc-400">Aucun type trouvé.</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {selectedType && (
                  <>
                    <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">
                        Champs à renseigner
                      </div>
                      <button
                        data-testid="run-ocr-btn"
                        onClick={runOCR}
                        disabled={ocrLoading}
                        className="inline-flex items-center gap-2 border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white text-xs font-medium px-3 py-1.5 transition-colors disabled:opacity-50"
                      >
                        {ocrLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                        {ocrLoading ? "Extraction..." : "Remplir via OCR (Gemini)"}
                      </button>
                    </div>

                    <div className="space-y-4">
                      {selectedType.fields.map((f) => (
                        <MetadataField
                          key={f.id}
                          field={f}
                          value={metadata[f.key] ?? ""}
                          onChange={(v) => setMetadata((prev) => ({ ...prev, [f.key]: v }))}
                          societes={societes}
                          entrepriseId={activeEntrepriseId}
                          onEnterpriseSelect={setActiveEntrepriseId}
                        />
                      ))}
                      {selectedType.fields.length === 0 && (
                        <div className="text-sm text-zinc-500">
                          Aucun champ défini pour ce type. L'administrateur peut en ajouter.
                        </div>
                      )}
                    </div>

                    <div className="pt-4 border-t border-zinc-100">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">
                        Aperçu du classement
                      </div>
                      <div className="mt-2 font-mono text-sm text-zinc-700 break-all bg-zinc-50 border border-zinc-200 p-3">
                        {selectedType.folder_template}/{selectedType.filename_template}.pdf
                      </div>
                    </div>

                    <button
                      data-testid="classify-submit"
                      onClick={() => classify(false)}
                      disabled={classifyLoading}
                      className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium py-3 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                      {classifyLoading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      {classifyLoading ? "Classement..." : "Classer ce document"}
                    </button>
                    <button
                      data-testid="queue-delete-selected"
                      onClick={() => {
                        const doc = docs.find(d => d.id === selectedId);
                        if (doc) deleteDoc(doc.id, doc.original_filename);
                      }}
                      disabled={deletingId === selectedId}
                      className="w-full border border-zinc-200 text-zinc-500 hover:border-[#DC2626] hover:text-[#DC2626] text-sm py-2.5 transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
                    >
                      {deletingId === selectedId
                        ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" />
                        : <Trash2 size={13} strokeWidth={1.5} />
                      }
                      Supprimer ce document
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
      {/* Duplicate classify warning dialog */}
      {duplicateWarning && (
        <DuplicateClassifyDialog
          warning={duplicateWarning}
          onCancel={() => setDuplicateWarning(null)}
          onForce={() => classify(true)}
          loading={classifyLoading}
        />
      )}
    </div>
  );
}
function toISODate(value, format) {
  if (!value) return "";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const iso = parseDateInput(s, format || "dd/mm/yyyy");
  return iso ? iso.slice(0, 10) : "";
}

function MetadataField({ field, value, onChange, societes = [], entrepriseId = null, onEnterpriseSelect }) {
  const [draft, setDraft] = useState(displayValue(value, field.field_type, field.format));
  const [touched, setTouched] = useState(false);

  // Sync down when external value changes (e.g. OCR auto-fill)
  useEffect(() => {
    setDraft(displayValue(value, field.field_type, field.format));
    setTouched(false);
  }, [value, field.field_type, field.format]);

  const common =
    "w-full bg-white border border-zinc-300 px-4 py-2.5 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none";

  const parsed = useMemo(() => parseValue(draft, field.field_type, field.format), [draft, field]);
  const invalid = touched && draft && parsed === null;
  const placeholder = placeholderFor(field.field_type, field.format);

  const handleBlur = () => {
    setTouched(true);
    if (draft === "") { onChange(""); return; }
    if (parsed !== null) {
      onChange(parsed);
      setDraft(displayValue(parsed, field.field_type, field.format));
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setDraft(v);
    const p = parseValue(v, field.field_type, field.format);
    onChange(p !== null ? p : v);
  };

  return (
    <label className="block">
      <span className="flex items-baseline justify-between mb-2">
        <span className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-mono">
          {field.label}{field.required && <span className="text-[#FF3B30]"> *</span>}
        </span>
        {field.format && (
          <span className="text-[10px] text-zinc-400 font-mono">format : {field.format}</span>
        )}
      </span>

      {field.field_type === "date" ? (
        <input
          data-testid={`meta-${field.key}`}
          type="date"
          value={toISODate(value, field.format)}
          onChange={(e) => { onChange(e.target.value); setDraft(e.target.value); }}
          className={common}
        />
      ) : (field.field_type === "contact" || field.field_type === "entreprise") ? (
        <BedeeSearchField
          data-testid={`meta-${field.key}`}
          type={field.field_type}
          value={draft}
          onChange={(v) => { setDraft(v); onChange(v); }}
          onSelect={field.field_type === "entreprise"
            ? (item) => onEnterpriseSelect?.(item.id)
            : undefined}
          entrepriseId={field.field_type === "contact" ? entrepriseId : undefined}
          placeholder={field.field_type === "entreprise" ? "Rechercher une entreprise Bedee…" : "Rechercher un contact Bedee…"}
          className={common}
        />
      ) : field.field_type === "societe" ? (
        <select
          data-testid={`meta-${field.key}`}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); onChange(e.target.value); }}
          className={`${common} cursor-pointer`}
        >
          <option value="">— Sélectionner une société —</option>
          {societes.map((s) => (
            <option key={s.id} value={s.nom}>{s.nom}{s.siren ? ` (${s.siren})` : ""}</option>
          ))}
        </select>
      ) : (
        <input
          data-testid={`meta-${field.key}`}
          type="text"
          value={draft}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`${common} ${invalid ? "border-[#DC2626] focus:border-[#DC2626] focus:ring-[#DC2626]" : ""}`}
          placeholder={placeholder}
          maxLength={200}
          inputMode={field.field_type === "number" ? "decimal" : "text"}
        />
      )}

      {invalid && (
        <span className="block mt-1 text-[10px] text-[#DC2626] font-mono">
          Format invalide. Attendu : {placeholder}
        </span>
      )}
    </label>
  );
}


// ── Duplicate Popover (queue: original filename match) ────────────────────
function DuplicatePopover({ duplicates, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      data-testid="dup-popover"
      className="absolute left-0 top-full mt-1 z-30 w-72 bg-white border border-amber-200 shadow-xl animate-fade-in-up"
    >
      <div className="px-3 py-2 border-b border-amber-100 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest font-mono text-amber-700 font-semibold">
          Doublons détectés
        </span>
        <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700">
          <X size={12} strokeWidth={2} />
        </button>
      </div>
      <div className="divide-y divide-zinc-100 max-h-52 overflow-y-auto">
        {duplicates.map((dup) => (
          <div key={dup.id} className="px-3 py-2.5">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className={`text-[9px] font-mono px-1.5 py-0.5 border ${
                dup.status === "classified"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : "bg-amber-50 border-amber-200 text-amber-700"
              }`}>
                {dup.status === "classified" ? "Classé" : "En attente"}
              </span>
            </div>
            <div className="text-xs font-medium text-zinc-800 truncate">{dup.original_filename}</div>
            {dup.classified_path && (
              <div className="text-[10px] font-mono text-zinc-400 truncate mt-0.5">
                {dup.classified_path}/{dup.classified_filename}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Duplicate Classify Dialog (classified_filename match) ─────────────────
function DuplicateClassifyDialog({ warning, onCancel, onForce, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div
        data-testid="dup-classify-dialog"
        className="relative bg-white border border-zinc-200 shadow-2xl w-full max-w-md mx-4"
      >
        <div className="px-5 py-4 border-b border-zinc-200 flex items-start gap-3">
          <AlertTriangle size={18} strokeWidth={1.5} className="text-amber-500 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-zinc-900">Nom de fichier déjà utilisé</div>
            <div className="text-sm text-zinc-500 mt-0.5">
              Un document avec le même nom existe déjà dans votre organisation.
            </div>
          </div>
        </div>

        <div className="px-5 py-4 bg-zinc-50 border-b border-zinc-200">
          <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">
            Nom généré
          </div>
          <div className="font-mono text-xs text-zinc-900 bg-white border border-zinc-200 px-3 py-2 break-all">
            {warning.path}/{warning.filename}
          </div>

          <div className="mt-3 text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">
            Fichier(s) identique(s) existant
          </div>
          <div className="space-y-2">
            {warning.duplicates.map((dup) => (
              <div key={dup.id} className="bg-white border border-zinc-200 px-3 py-2">
                <div className="text-xs font-medium text-zinc-800 truncate">{dup.classified_filename}</div>
                {dup.classified_path && (
                  <div className="text-[10px] font-mono text-zinc-400 truncate mt-0.5 flex items-center gap-1">
                    <ArrowRight size={9} strokeWidth={1.5} className="shrink-0" />
                    {dup.classified_path}
                  </div>
                )}
                <div className="text-[10px] text-zinc-400 mt-0.5">
                  Fichier source : {dup.original_filename}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-5 py-4 flex justify-end gap-2">
          <button
            data-testid="dup-cancel-btn"
            onClick={onCancel}
            className="border border-zinc-300 text-sm px-4 py-2 hover:bg-zinc-100 transition-colors"
          >
            Annuler
          </button>
          <button
            data-testid="dup-force-btn"
            onClick={onForce}
            disabled={loading}
            className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-5 py-2 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <AlertTriangle size={13} strokeWidth={2} />}
            Classer quand même
          </button>
        </div>
      </div>
    </div>
  );
}
